import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {mapGetters, mapState} from 'vuex';
import localeData from 'dayjs/plugin/localeData';
import updateLocale from 'dayjs/plugin/updateLocale';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import timezone from 'dayjs/plugin/timezone';

// in every place where we would like set locale - dayjs.locale(...) all locales should be imported
import 'dayjs/locale/de';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/it';
import 'dayjs/locale/nl';

dayjs.extend(utc);
dayjs.extend(updateLocale);
dayjs.extend(localeData);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(isoWeek);
dayjs.extend(timezone);

dayjs.updateLocale('fr', {
  weekdays: [
    'Dimanche',
    'Lundi',
    'Mardi',
    'Mercredi',
    'Jeudi',
    'Vendredi',
    'Samedi',
  ],
  monthsShort: [
    'Jan',
    'Fév',
    'Mars',
    'Avr',
    'Mai',
    'Juin',
    'Juil',
    'Août',
    'Sept',
    'Oct',
    'Nov',
    'Déc',
  ],
});

dayjs.updateLocale('en', {
  weekdays: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  monthsShort: [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'June',
    'July',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ],
});

dayjs.updateLocale('de', {
  weekdays: [
    'Sonntag',
    'Montag',
    'Dienstag',
    'Mittwoch',
    'Donnerstag',
    'Freitag',
    'Samstag',
  ],
  monthsShort: [
    'Jan',
    'Feb',
    'März',
    'Apr',
    'Mai',
    'Juni',
    'Juli',
    'Aug',
    'Sep',
    'Okt',
    'Nov',
    'Dez',
  ],
});

dayjs.updateLocale('es', {
  weekdays: [
    'Domingo',
    'Lunes',
    'Martes',
    'Miércoles',
    'Jueves',
    'Viernes',
    'Sábado',
  ],
  monthsShort: [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic',
  ],
});

dayjs.updateLocale('it', {
  weekdays: [
    'Domenica',
    'Lunedì',
    'Martedì',
    'Mercoledì',
    'Giovedì',
    'Venerdì',
    'Sabato',
  ],
  monthsShort: [
    'Gen',
    'Feb',
    'Mar',
    'Apr',
    'Mag',
    'Giu',
    'Lug',
    'Ago',
    'Set',
    'Ott',
    'Nov',
    'Dic',
  ],
});

dayjs.updateLocale('nl', {
  weekdays: [
    'Zondag',
    'Maandag',
    'Dinsdag',
    'Woensdag',
    'Donderdag',
    'Vrijdag',
    'Zaterdag',
  ],
  monthsShort: [
    'Jan',
    'Feb',
    'Mrt',
    'Apr',
    'Mei',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Okt',
    'Nov',
    'Dec',
  ],
});

export const datesMixin = {
  data() {
    return {
      DDMMYYYY: 'DD/MM/YYYY',
      DDMMYYYY_DASH: 'DD-MM-YYYY',
      DDMMYYYYAHHMM: 'DD/MM/YYYY à HH:mm',
      YYYYMMDD: 'YYYY/MM/DD',
      YYYYMM: 'YYYY MM',
      MMYYYY: 'MM YYYY',
      shortMonths: dayjs.monthsShort(),
      daysWeek: dayjs.weekdays(),
    };
  },
  computed: {
    ...mapState('user', ['preferences']),
    ...mapGetters('user', ['getLocale']),
    formattedDate() {
      return (date, format, local = false) =>
        (local ? dayjs(date).local() : dayjs(date)).format(format);
    },
    locale() {
      return this.preferences?.locale;
    },
  },
  watch: {
    locale: {
      handler() {
        if (dayjs.locale() !== this.getLocale) {
          dayjs.locale(this.getLocale);
        }
        this.shortMonths = dayjs.monthsShort();
        this.daysWeek = dayjs.weekdays();
      },
    },
  },
  methods: {
    dateInMonths(date) {
      return date.year() * 12 + date.month();
    },
    endOfDayISO(date) {
      if (!date) return null;
      let ymd;
      // Dayjs or Moment object
      if (typeof date === 'object' && typeof date.format === 'function') {
        ymd = date.format('YYYY-MM-DD');
        // Native Date
      } else if (date instanceof Date) {
        ymd = dayjs(date).format('YYYY-MM-DD');
        // String (ISO or date-only)
      } else if (typeof date === 'string') {
        ymd = date.slice(0, 10);
      }
      if (!ymd) return null;
      return dayjs(ymd, 'YYYY-MM-DD')
        .endOf('day')
        .subtract(1, 'hour')
        .format('YYYY-MM-DD HH:mm:ss');
    },
    isoDateWithTimeZoneOffset(date) {
      const dtObj = new Date(date);
      return new Date(
        dtObj.getTime() + Math.abs(dtObj.getTimezoneOffset() * 60000),
      ).toISOString();
    },
    // TODO migrate to dayjs and remove moment once we have setup test with vite
    getDate(initialString, dateFormat, utc, convention, endOfDay) {
      let date = null;
      if (initialString) {
        if (dateFormat) {
          if (utc) {
            date = dayjs.utc(initialString, dateFormat);
          } else {
            date = dayjs(initialString, dateFormat);
          }
          if (endOfDay) {
            date = date.endOf('day');
          }
          return date;
        }
        date = utc ? dayjs.utc(initialString) : dayjs(initialString);
        return date;
      }
      if (utc) {
        date = dayjs.utc();
      } else if (convention) {
        date = dayjs().hour(3).minute(0).second(0);
      }
      if (date) {
        return date;
      }
      return dayjs();
    },
    getDayjsDate(date, dateFormat, utc) {
      if (dateFormat) {
        if (utc) {
          return dayjs(date).utc().format(dateFormat);
        }
        return dayjs(date, dateFormat);
      }
      if (utc) {
        return dayjs(date).utc();
      }
      return dayjs(date);
    },
    getDayjsDateAndFormat(date, format) {
      return this.getDayjsDate(date).format(format);
    },
    formatDayjsDateAndHours(datejsDate) {
      return `${datejsDate.format('L')} ${datejsDate.format('LT')}`;
    },
    getUTCDate(initialString) {
      return initialString ? dayjs.utc(initialString) : dayjs.utc();
    },
    dateToISO(initialDate) {
      return initialDate.toISOString();
    },
    getMonthIndexInDatePeriod(dateFrom, dateTo) {
      let startDate = this.getDate(dateFrom);
      const endDate = this.getDate(dateTo);
      const currMonth = `${this.getDate(new Date()).month()}-${this.getDate(new Date()).year()}`;
      const datesArr = [];
      if (endDate.isBefore(startDate)) {
        return -1;
      }
      while (startDate.isSameOrBefore(endDate)) {
        datesArr.push(`${startDate.month()}-${startDate.year()}`);
        startDate = startDate.add(1, 'month');
      }
      return datesArr.findIndex((itm) => itm === currMonth);
    },
  },
};

export default {datesMixin};
